import React, { useState } from 'react';
import { ConfigProvider, theme, Table, Avatar, Checkbox, Space, Button } from 'antd';
import {
    Plus,
    Menu,
    LayoutList,
    Tag,
    MessageSquare,
    ArrowRightToLine,
    ArrowLeftToLine,
    Settings,
    Moon,
    Sun
} from 'lucide-react';
import Layout from '../../Layout'
import { useNavigate } from 'react-router-dom';
import { PlusOutlined } from "@ant-design/icons";
import { Link } from 'react-router-dom';

export default function ContractList() {
    const [isDarkMode, setIsDarkMode] = useState(false);
    const [activeTab, setActiveTab] = useState('all');
    const navigate = useNavigate();

    // Cấu hình Theme Ant Design để bảng nhìn phẳng và khớp với ảnh mẫu
    const antdThemeConfig = {
        token: {
            colorPrimary: '#3e91ab', // Màu chủ đạo #3e91ab
            colorBgContainer: isDarkMode ? '#1f2937' : '#ffffff',
            colorText: isDarkMode ? '#f3f4f6' : '#374151',
            borderRadius: 4,
        },
        components: {
            Table: {
                headerBg: 'transparent',
                headerColor: '#6b7280',
                headerBorderRadius: 0,
                rowHoverBg: isDarkMode ? '#374151' : '#f9fafb',
                paddingContentVertical: 8, // Thu nhỏ padding để bảng gọn
            }
        },
        algorithm: isDarkMode ? theme.darkAlgorithm : theme.defaultAlgorithm,
    };

    // Mock data bám sát hình ảnh mẫu
    const dataSource = [
        { key: '1', creator: { img: 'https://i.pravatar.cc/150?u=1' }, code: 'NTL-ncc02', name: 'Hợp đồng mua hàng sản phẩm/dịch vụ', supplier: null, assignee: null, status: 'active', date: '07/07/2026' },
        { key: '2', creator: { text: '1', color: '#ff6b6b' }, code: 'NTL-ncc01', name: 'Hợp đồng mua hàng sản phẩm/dịch vụ', supplier: null, assignee: null, status: 'active', date: '07/07/2026' },
        { key: '3', creator: { text: '1', color: '#ff6b6b' }, code: 'NTL001', name: 'Hợp đồng mua hàng sản phẩm/dịch vụ', supplier: 'Công ty TNHH ABC', assignee: null, status: 'inactive', date: '06/07/2026' },
        { key: '4', creator: { img: 'https://i.pravatar.cc/150?u=2' }, code: '1298833', name: 'Hợp đồng mua hàng sản phẩm/dịch vụ', supplier: 'Thu Nhi EatClean', assignee: null, status: 'inactive', date: '25/06/2026' },
        { key: '5', creator: { img: 'https://i.pravatar.cc/150?u=3' }, code: 'PLASTIQ - COCOON - 19/06/2026', name: 'Hợp đồng mua hàng sản phẩm/dịch vụ', supplier: 'NGUYỄN NGỌC THÚY', assignee: 'Phương Anh', status: 'inactive', date: '19/06/2026' },
        { key: '6', creator: { text: 'T', color: '#0ea5e9' }, code: '190620', name: 'Hợp đồng mua hàng sản phẩm/dịch vụ', supplier: 'NGUYỄN NGỌC THÚY', assignee: null, status: 'inactive', date: '19/06/2026' },
        { key: '7', creator: { img: 'https://i.pravatar.cc/150?u=4' }, code: '20062026', name: 'Hợp đồng mua hàng sản phẩm/dịch vụ', supplier: 'Trần Thị Huệ', assignee: null, status: 'inactive', date: '18/06/2026' },
        { key: '8', creator: { text: '1', color: '#ff6b6b' }, code: 'test', name: 'Hợp đồng mua bán tủ lạnh', supplier: null, assignee: null, status: 'inactive', date: '18/04/2026' },
        { key: '9', creator: { text: '1', color: '#ff6b6b' }, code: '19.08.2025_1', name: 'Hợp đồng mua bán tủ lạnh', supplier: null, assignee: null, status: 'active', date: '13/03/2026' },
        { key: '10', creator: { img: 'https://i.pravatar.cc/150?u=5' }, code: '0010503', name: 'Hợp đồng mua test', supplier: null, assignee: null, status: 'inactive', date: '05/03/2026' },
        { key: '11', creator: { img: 'https://i.pravatar.cc/150?u=6' }, code: '102025/HDMH/3', name: 'Hợp đồng nguyên tắc - Mua sản phẩm/dịch vụ', supplier: null, assignee: null, status: 'inactive', date: '18/10/2025' },
        { key: '12', creator: { text: 'H', color: '#84cc16' }, code: '092025/HDMB/7', name: 'Hợp đồng mua hàng sản phẩm/dịch vụ', supplier: 'son', assignee: null, status: 'active', date: '09/09/2025' },
    ];

    // Định nghĩa các cột (Columns)
    const columns = [
        {
            title: 'Người tạo',
            dataIndex: 'creator',
            key: 'creator',
            width: 90,
            render: (creator) => (
                <Link to="/sales-contract/detail" className="inline-block transition-opacity hover:opacity-80">
                    {creator.img ? (
                        <Avatar src={creator.img} size={28} className="cursor-pointer" />
                    ) : (
                        <Avatar style={{ backgroundColor: creator.color }} size={28} className="cursor-pointer">
                            {creator.text}
                        </Avatar>
                    )}
                </Link>
            ),
        },
        { title: 'Mã hợp đồng', dataIndex: 'code', key: 'code', className: 'font-medium' },
        { title: 'Tên hợp đồng', dataIndex: 'name', key: 'name' },
        { title: 'Nhà cung cấp', dataIndex: 'supplier', key: 'supplier', render: (text) => text || <span className="text-gray-400">--</span> },
        { title: 'Người phụ trách', dataIndex: 'assignee', key: 'assignee', render: (text) => text || <span className="text-gray-400">--</span> },
        {
            title: 'Trạng thái',
            dataIndex: 'status',
            key: 'status',
            align: 'center',
            render: (status) => (
                <span className={`px-2 py-1 rounded text-xs font-medium inline-block w-28 text-center ${status === 'active'
                    ? 'bg-green-100 text-green-600 dark:bg-green-900/30 dark:text-green-400'
                    : 'bg-gray-100 text-gray-500 dark:bg-gray-700/50 dark:text-gray-400'
                    }`}>
                    {status === 'active' ? 'Đang hiệu lực' : 'Chưa hiệu lực'}
                </span>
            ),
        },
        { title: 'Ngày ký', dataIndex: 'date', key: 'date', align: 'right' },
    ];

    return (
        <Layout>
            <div className="flex gap-2 flex justify-between">
                <h2 className="text-lg text-gray-900 font-semibold t-text-white">
                    Danh sách hợp đồng mua
                </h2>
                <div className="items-center gap-4 flex sm:hidden">
                    <Button
                        type="primary"
                        icon={<PlusOutlined />}
                        className="bg-[#3e91ab] hover:!bg-[#357f96] border-[#3e91ab]"
                        onClick={() => navigate(`/sales-contract/add`)}
                    >
                        Thêm mới
                    </Button>
                </div>
            </div>
            {/* Wrapper class có chứa tên page hiện tại để tiện viết CSS */}
            <div className={`contract-list-page min-h-screen transition-colors duration-300`}>

                {/* Box chứa bảng */}
                <div className={`w-full overflow-hidden flex flex-col rounded-md`}>

                    {/* HEADER TABS & NÚT THÊM MỚI */}
                    <div className={`flex justify-between items-end pt-3 border-b dark:border-[#374151]`}>
                        <div className="overflow-x-auto no-scrollbar">
                            <div className="flex gap-6 text-sm font-medium min-w-[400px] pb-3">
                                <div
                                    onClick={() => setActiveTab('all')}
                                    className={`cursor-pointer transition-colors ${activeTab === 'all' ? 'text-[#3e91ab]' : 'text-gray-500 hover:text-gray-700 dark:text-gray-400'}`}
                                >
                                    Tất cả (20)
                                </div>
                                <div
                                    onClick={() => setActiveTab('mine')}
                                    className={`cursor-pointer transition-colors ${activeTab === 'mine' ? 'text-[#3e91ab]' : 'text-gray-500 hover:text-gray-700 dark:text-gray-400'}`}
                                >
                                    Bạn phụ trách (0)
                                </div>
                                <div
                                    onClick={() => setActiveTab('department')}
                                    className={`cursor-pointer transition-colors ${activeTab === 'department' ? 'text-[#3e91ab]' : 'text-gray-500 hover:text-gray-700 dark:text-gray-400'}`}
                                >
                                    Phòng ban bạn (0)
                                </div>
                            </div>
                        </div>

                        <div className="items-center gap-4 pb-2 hidden sm:flex">
                            <Button
                                type="primary"
                                icon={<PlusOutlined />}
                                className="bg-[#3e91ab] hover:!bg-[#357f96] border-[#3e91ab]"
                                onClick={() => navigate(`/sales-contract/add`)}
                            >
                                Thêm mới
                            </Button>
                        </div>
                    </div>

                    {/* THANH TOOLBAR & PHÂN TRANG */}
                    <div className={`flex flex-col xl:flex-row items-center py-2 text-xs md:text-sm text-black/80 border-b dark:border-[#374151] overflow-x-auto`}>

                        {/* Thay justify-end bằng ml-auto (đẩy sang phải), và dùng min-w-max để nội dung không bị rớt dòng */}
                        <div className="flex items-center gap-4 xl:gap-5 w-full xl:w-auto ml-auto min-w-max text-000000e0">
                            <span className="flex items-center gap-1.5 cursor-pointer hover:text-[#3e91ab] transition-colors"><Tag size={14} /> Nhãn</span>
                            <span className="flex items-center gap-1.5 cursor-pointer hover:text-[#3e91ab] transition-colors"><MessageSquare size={14} /> Sms/Zalo</span>
                            <span className="flex items-center gap-1.5 cursor-pointer hover:text-[#3e91ab] transition-colors"><ArrowRightToLine size={14} /> Export</span>
                            <span className="flex items-center gap-1.5 cursor-pointer hover:text-[#3e91ab] transition-colors"><ArrowLeftToLine size={14} /> Import</span>
                            <span className="flex items-center gap-1.5 cursor-pointer hover:text-[#3e91ab] transition-colors"><Settings size={14} /> Cài đặt</span>
                        </div>

                    </div>

                    {/* BẢNG DỮ LIỆU */}
                    <div className="overflow-x-auto">
                        <Table
                            columns={columns}
                            dataSource={dataSource}
                            rowSelection={{ type: 'checkbox' }}
                            pagination={false}
                            size="small"
                            className="custom-ant-table"
                            scroll={{ x: "max-content" }}
                        />
                    </div>

                </div>
            </div>
        </Layout>
    );
}